import { forwardRef, type RefObject } from 'react'
import { AndrzejHeader } from './AndrzejHeader'
import { ConversationList } from './ConversationList'
import { MessageList } from './MessageList'
import { ChatInput } from './ChatInput'
import type { AvatarState, Conversation } from '@/types/andrzej'
import type { DisplayMessage } from '@/hooks/useAndrzej'

interface Props {
  isClosing: boolean
  messages: DisplayMessage[]
  isThinking: boolean
  avatarState: AvatarState
  welcomeMessage: string
  showWelcome: boolean
  conversations: Conversation[]
  isConvosPanelOpen: boolean
  conversationId: number | null
  photoPreviews: string[]
  messagesEndRef: RefObject<HTMLDivElement | null>
  onSend: (content: string) => void
  onAddPhoto: (file: File) => void
  onRemovePhoto: (index: number) => void
  onClose: () => void
  onNewConversation: () => void
  onToggleConversations: () => void
  onSelectConversation: (id: number) => void
}

export const AndrzejSidebar = forwardRef<HTMLElement, Props>(function AndrzejSidebar(props, ref) {
  return (
    <aside
      ref={ref}
      className={`fixed top-0 right-0 z-40 flex h-full w-full flex-col sm:w-[340px] bg-paper ${
        props.isClosing ? 'animate-[sidebar-out_0.2s_ease-in_forwards]' : 'animate-[sidebar-in_0.2s_ease-out]'
      }`}
      style={{ boxShadow: '-2px 0 12px rgba(58,42,24,0.15)' }}
    >
      <AndrzejHeader
        avatarState={props.avatarState}
        onNewConversation={props.onNewConversation}
        onToggleConversations={props.onToggleConversations}
        onClose={props.onClose}
      />

      {props.isConvosPanelOpen && (
        <div className="border-b border-desk max-h-48 overflow-y-auto bg-paper-dark">
          <ConversationList
            conversations={props.conversations}
            activeId={props.conversationId}
            onSelect={props.onSelectConversation}
          />
        </div>
      )}

      <MessageList
        messages={props.messages}
        isThinking={props.isThinking}
        welcomeMessage={props.welcomeMessage}
        showWelcome={props.showWelcome}
        messagesEndRef={props.messagesEndRef}
        onAddPhoto={props.onAddPhoto}
      />

      <ChatInput
        onSend={props.onSend}
        onAddPhoto={props.onAddPhoto}
        onRemovePhoto={props.onRemovePhoto}
        photoPreviews={props.photoPreviews}
        disabled={props.isThinking}
      />
    </aside>
  )
})
